import { inject, Injectable } from '@angular/core';
import { AuthUser, SystemInfo } from '@common/models';
import { ConfigurationService } from '@common/services/configuration.service';
import { getSentryEnvironment } from '@common/utils';
import { environment } from '@env/environment';
import * as Sentry from '@sentry/angular';

const IGNORED_ERRORS: RegExp[] = [
  // MetaMask extension not found
  /MetaMask/i,
  // browser extension calling chrome.runtime.onMessage from the page context; the app has no onMessage
  /reading 'onMessage'/,
  // Firefox: chart.js MutationObserver on document touches a node injected by an extension
  /Permission denied to access property "contains"/
];

@Injectable({ providedIn: 'root' })
export class SentryService {
  private readonly config = inject(ConfigurationService);

  init(info: SystemInfo): void {
    if (!this.config.get('production')) return;

    Sentry.init({
      dsn: environment.sentry.dsn,
      release: info.AssemblyVersion,
      ignoreErrors: IGNORED_ERRORS,
      integrations: [
        Sentry.breadcrumbsIntegration({
          console: false,
          dom: true,
          fetch: false,
          history: false,
          xhr: false
        }),
        Sentry.browserTracingIntegration({
          instrumentNavigation: true,
          instrumentPageLoad: true
        })
      ],
      beforeBreadcrumb: (breadcrumb, hint?) => {
        if (breadcrumb.category === 'ui.click') {
          const innerText = hint?.event?.target?.innerText;

          innerText && (breadcrumb.data = { target: innerText });
        }

        return breadcrumb;
      },
      tracesSampleRate: 1,
      environment: getSentryEnvironment()
    });

    Sentry.getGlobalScope().setTag('environment', getSentryEnvironment());
  }

  addUser(user: AuthUser): void {
    if (!this.config.get('production')) return;

    /** Add a user data for Sentry */
    const scope = Sentry.getGlobalScope();
    const { Email, DomainId, DomainName, Id, Name, Provider, RestoreEnable, SignInEnable } = user;

    scope.setUser({ Email, DomainId, DomainName, Id, Name, Provider, RestoreEnable, SignInEnable });
  }
}
